import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { plannedAccrualOrders, type PlannedAccrualOrder } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Список «Накази про зміну нарахувань» (фіча #3).
 *
 * Редактор — окрема сторінка (PlannedAccrualOrderEditor) за спільним патерном
 * кадрових наказів. Тут лише перелік + перехід у редактор.
 */
export default function PlannedAccrualOrdersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // За замовч. показуємо накази ВСIХ юросіб (щойно створений видно одразу).
  const [searchParams, setSearchParams] = useSearchParams();
  const orgId = searchParams.get('org') ?? '';
  const setOrgId = (v: string) =>
    setSearchParams((p) => { v ? p.set('org', v) : p.delete('org'); return p; });

  const list = useQuery({
    queryKey: ['planned-accrual-orders', orgId],
    queryFn: () => plannedAccrualOrders.list({ organization_id: orgId || undefined, limit: 200 }),
  });

  // Deep-link: картка працівника → «Підстава» → ?open={id} → одразу у редактор.
  const openId = searchParams.get('open');
  useEffect(() => {
    if (openId) navigate(`/payroll/planned-accrual-orders/${openId}`, { replace: true });
  }, [openId, navigate]);

  const post = useMutation({
    mutationFn: (id: string) => plannedAccrualOrders.post(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planned-accrual-orders', orgId] }),
  });
  const unpost = useMutation({
    mutationFn: (id: string) => plannedAccrualOrders.unpost(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planned-accrual-orders', orgId] }),
  });

  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])),
    [orgList.data],
  );
  const rows = list.data?.items ?? [];

  const cols = useMemo<ColumnDef<PlannedAccrualOrder>[]>(() => [
    {
      key: 'document_number', header: '№', width: 130, importBadge: true,
      sortValue: (d) => d.document_number ?? '',
      cell: (d) => (
        <a style={{ cursor: 'pointer' }}
           onClick={() => navigate(`/payroll/planned-accrual-orders/${d.id}`)}>
          {d.document_number}
        </a>
      ),
    },
    {
      key: 'document_date', header: 'Дата', width: 120,
      sortValue: (d) => d.document_date,
      cell: (d) => new Date(d.document_date).toLocaleDateString('uk-UA'),
    },
    {
      key: 'effective_date', header: 'Діє з', width: 120,
      sortValue: (d) => d.effective_date,
      cell: (d) => new Date(d.effective_date).toLocaleDateString('uk-UA'),
    },
    {
      key: 'org', header: 'Організація', width: 200,
      sortValue: (d) => (orgName.get(d.organization_id) ?? '').toLowerCase(),
      cell: (d) => orgName.get(d.organization_id) ?? '—',
    },
    {
      // Один рядок → ПIБ працівника; кілька → «масовий».
      key: 'pib', header: 'ПIБ', width: 220,
      sortValue: (d) => d.items.length === 1
        ? (d.items[0]?.employee_name ?? '').toLowerCase() : 'масовий',
      cell: (d) => d.items.length === 1
        ? (d.items[0]?.employee_name ?? '—')
        : <span style={{ color: 'var(--text-secondary)' }}>масовий ({d.items.length})</span>,
    },
    {
      key: 'status', header: 'Статус', width: 140,
      sortValue: (d) => (d.posted ? 1 : 0),
      cell: (d) => (d.posted ? '✓ проведено' : 'чернетка'),
    },
    {
      key: 'actions', header: '', width: 130, align: 'right',
      cell: (d) => (
        d.posted ? (
          <button onClick={() => unpost.mutate(d.id)} disabled={unpost.isPending}>Розпровести</button>
        ) : (
          <button className="primary" onClick={() => post.mutate(d.id)} disabled={post.isPending}>Провести</button>
        )
      ),
    },
  ], [post, unpost, orgName, navigate]);

  return (
    <>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Накази про зміну нарахувань</h2>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 'auto' }}>
          <option value="">Усі юрособи</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <button className="primary" style={{ marginLeft: 'auto' }}
                onClick={() => navigate('/payroll/planned-accrual-orders/new')}>
          + Новий документ
        </button>
      </div>

      <DataTable<PlannedAccrualOrder>
        rows={rows}
        columns={cols}
        rowKey={(d) => d.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(d) => navigate(`/payroll/planned-accrual-orders/${d.id}`)}
        rowMuted={(d) => d.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_number"
      />
    </>
  );
}
